import React, { useState, useMemo, useEffect, useRef, useContext } from "react";
import { FaTasks, FaListUl } from "react-icons/fa";
import { useT } from "../../contexts/TranslationContext";
import axios from "../../utils/axiosInstance";
import { displayText } from "../../utils/translationDisplay";
import { UserContext } from "../../utils/userContext";

/**
 * Format hours display for tasks (e.g., "25.5 / 40h" or "25.5h")
 */
const formatHoursDisplay = (tracked, estimated) => {
  const trackedNum = tracked || 0;
  const hasEstimate = estimated !== undefined && estimated !== null && estimated > 0;
  
  if (!hasEstimate && trackedNum === 0) return null;
  
  if (hasEstimate) {
    return `${trackedNum.toFixed(1)} / ${estimated.toFixed(1)}h`;
  }
  return `${trackedNum.toFixed(1)}h`;
};

/**
 * Modal for selecting tasks/subtasks from a project's task bundle
 * Mobile-optimized with tabs for Recent and All Tasks
 */
function TaskPickerModal({ isOpen, onClose, onSelect, bundle, recentTasks = [], onBundleUpdated, onReportMissingTask }) {
  const { t } = useT();
  const { user } = useContext(UserContext);
  // Writing to a project's task list is admin-only on the server. Workers raise a
  // missing-task flag instead, which routes through admin review.
  const canEditBundle = !!onBundleUpdated && !!user?.isAdmin;
  // The two are mutually exclusive: whoever cannot add the task themselves reports it missing.
  const canReportMissingTask = !!onReportMissingTask && !canEditBundle;
  // Workers log time against open work. Done tasks and subtasks stay visible for admins.
  const hideDoneTasks = !user?.isAdmin;
  const [activeTab, setActiveTab] = useState("recent");
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedTasks, setExpandedTasks] = useState({});
  const [expandedTaskSets, setExpandedTaskSets] = useState({});
  const [expandedRecentItem, setExpandedRecentItem] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  // Add task / add subtask (only in All tasks tab when onBundleUpdated is provided)
  const [showAddTaskForm, setShowAddTaskForm] = useState(false);
  const [addSubtaskForTaskIdx, setAddSubtaskForTaskIdx] = useState(null);
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newTaskDescription, setNewTaskDescription] = useState("");
  const [newSubtaskTitle, setNewSubtaskTitle] = useState("");
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState("");
  const [deletingTaskIdx, setDeletingTaskIdx] = useState(null);
  const [deletingSubtask, setDeletingSubtask] = useState(null); // { taskIdx, subtaskIdx }
  const prevOpenRef = useRef(false);

  const isDeletingTask = (taskIdx) => deletingTaskIdx === taskIdx;
  const isDeletingSubtask = (taskIdx, subtaskIdx) =>
    deletingSubtask?.taskIdx === taskIdx && deletingSubtask?.subtaskIdx === subtaskIdx;

  // Reset state only when modal opens (not when bundle updates after adding a task)
  useEffect(() => {
    if (isOpen && !prevOpenRef.current) {
      setSearchQuery("");
      setActiveTab(recentTasks.length > 0 ? "recent" : "all");
      setExpandedRecentItem(null);
      setSelectedItems([]);
      setShowAddTaskForm(false);
      setAddSubtaskForTaskIdx(null);
      setNewTaskTitle("");
      setNewTaskDescription("");
      setNewSubtaskTitle("");
      setAddError("");
      setDeletingTaskIdx(null);
      setDeletingSubtask(null);
    }
    prevOpenRef.current = isOpen;
  }, [isOpen, recentTasks.length]);

  // When modal is open, keep expansion in sync with bundle (e.g. after adding task/subtask) without switching tab
  useEffect(() => {
    if (!isOpen || !bundle) return;
    if (bundle._id) {
      setExpandedTaskSets(prev => ({ ...prev, [bundle._id]: true }));
    }
    if (bundle.tasks) {
      setExpandedTasks(prev => {
        const next = { ...prev };
        bundle.tasks.forEach(task => {
          if (task.subtasks && task.subtasks.length > 0) {
            next[task._id] = true;
          }
        });
        return next;
      });
    }
  }, [isOpen, bundle]);

  // Build flat list of selectable items (tasks and subtasks)
  const allItems = useMemo(() => {
    if (!bundle?.tasks) return [];
    
    const items = [];
    bundle.tasks.forEach((task, taskIdx) => {
      if (hideDoneTasks && task.status === "done") return;

      // Always add the task itself as selectable (even if it has subtasks)
      items.push({
        type: "task",
        taskId: task._id,
        taskIdx,
        title: task.title,
        description: task.description || "",
        displayName: task.title,
        titleDisplay: task.titleDisplay || task.title,
        descriptionDisplay: task.descriptionDisplay != null ? task.descriptionDisplay : (task.description || ""),
        titleLanguage: task.titleLanguage,
        bundleId: bundle._id,
        taskSetName: bundle.taskSetRef?.name || "",
        showDescriptionInTimeReport: task.showDescriptionInTimeReport || false,
        requireDescriptionInTimeReport: task.requireDescriptionInTimeReport || false,
        billingModel: task.billingModel || null,
        estimatedHours: task.estimatedHours,
        trackedHours: task.trackedHours || 0,
      });
      
      // If task has subtasks, also add each subtask as selectable
      if (task.subtasks && task.subtasks.length > 0) {
        task.subtasks.forEach((subtask, subtaskIdx) => {
          if (hideDoneTasks && subtask.status === "done") return;
          items.push({
            type: "subtask",
            taskId: task._id,
            taskIdx,
            subtaskId: subtask._id,
            subtaskIdx,
            taskTitle: task.title,
            taskTitleDisplay: task.titleDisplay || task.title,
            title: subtask.title,
            description: subtask.description || "",
            displayName: `${task.title} > ${subtask.title}`,
            titleDisplay: subtask.titleDisplay || subtask.title,
            descriptionDisplay: subtask.descriptionDisplay != null ? subtask.descriptionDisplay : (subtask.description || ""),
            titleLanguage: subtask.titleLanguage || task.titleLanguage,
            bundleId: bundle._id,
            taskSetName: bundle.taskSetRef?.name || "",
            showDescriptionInTimeReport: subtask.showDescriptionInTimeReport || false,
            requireDescriptionInTimeReport: subtask.requireDescriptionInTimeReport || false,
            billingModel: subtask.billingModel || task.billingModel || null,
            estimatedHours: subtask.estimatedHours,
            trackedHours: subtask.trackedHours || 0,
          });
        });
      }
    });
    return items;
  }, [bundle, hideDoneTasks]);

  // Filter items by search query
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return allItems;
    const query = searchQuery.toLowerCase();
    return allItems.filter(item => 
      item.displayName.toLowerCase().includes(query)
    );
  }, [allItems, searchQuery]);

  // Group tasks for accordion view
  const groupedTasks = useMemo(() => {
    if (!bundle?.tasks) return [];
    return bundle.tasks.flatMap((task, taskIdx) => {
      if (hideDoneTasks && task.status === "done") return [];
      const subtasks = hideDoneTasks
        ? (task.subtasks || []).filter((subtask) => subtask.status !== "done")
        : task.subtasks;
      return [{ ...task, taskIdx, bundleId: bundle._id, subtasks }];
    });
  }, [bundle, hideDoneTasks]);

  // Filter recent tasks to only include those in current bundle, enrich with current task data (including translated title/description), limit to 5 latest
  const RECENT_TASKS_LIMIT = 5;
  const filteredRecentTasks = useMemo(() => {
    if (!bundle?._id || !bundle?.tasks) return [];

    return recentTasks
      .filter(rt => rt.bundleId === bundle._id)
      .map(rt => {
        const currentTask = bundle.tasks.find(t => t._id.toString() === rt.taskId.toString());
        if (!currentTask) return rt;
        if (hideDoneTasks && currentTask.status === "done") return null;

        if (rt.subtaskId) {
          const currentSubtask = currentTask.subtasks?.find(st => st._id.toString() === rt.subtaskId.toString());
          if (currentSubtask) {
            if (hideDoneTasks && currentSubtask.status === "done") return null;
            return {
              ...rt,
              title: currentSubtask.title || rt.title,
              titleDisplay: currentSubtask.titleDisplay ?? currentSubtask.title ?? rt.title,
              description: currentSubtask.description || rt.description,
              descriptionDisplay: currentSubtask.descriptionDisplay ?? currentSubtask.description ?? rt.description,
              taskTitle: currentTask.title || rt.taskTitle,
              taskTitleDisplay: currentTask.titleDisplay ?? currentTask.title ?? rt.taskTitle,
              showDescriptionInTimeReport: currentSubtask.showDescriptionInTimeReport || rt.showDescriptionInTimeReport,
              requireDescriptionInTimeReport: currentSubtask.requireDescriptionInTimeReport || rt.requireDescriptionInTimeReport,
              billingModel: currentSubtask.billingModel || currentTask.billingModel || rt.billingModel || null,
            };
          }
        }

        return {
          ...rt,
          title: currentTask.title || rt.title,
          titleDisplay: currentTask.titleDisplay ?? currentTask.title ?? rt.title,
          description: currentTask.description || rt.description,
          descriptionDisplay: currentTask.descriptionDisplay ?? currentTask.description ?? rt.description,
          showDescriptionInTimeReport: currentTask.showDescriptionInTimeReport || rt.showDescriptionInTimeReport,
          requireDescriptionInTimeReport: currentTask.requireDescriptionInTimeReport || rt.requireDescriptionInTimeReport,
          billingModel: currentTask.billingModel || rt.billingModel || null,
        };
      })
      .filter(Boolean)
      .slice(0, RECENT_TASKS_LIMIT);
  }, [recentTasks, bundle, hideDoneTasks]);

  const toggleTaskExpand = (taskId) => {
    setExpandedTasks(prev => ({
      ...prev,
      [taskId]: !prev[taskId]
    }));
  };

  const toggleTaskSetExpand = (bundleId) => {
    setExpandedTaskSets(prev => ({
      ...prev,
      [bundleId]: !prev[bundleId]
    }));
  };

  const isItemSelected = (item) => {
    return selectedItems.some(
      s => s.taskId === item.taskId && s.subtaskId === item.subtaskId
    );
  };

  const toggleItemSelection = (item) => {
    if (isItemSelected(item)) {
      setSelectedItems(selectedItems.filter(
        s => !(s.taskId === item.taskId && s.subtaskId === item.subtaskId)
      ));
    } else {
      setSelectedItems([...selectedItems, item]);
    }
  };

  const handleConfirmSelection = () => {
    selectedItems.forEach(item => onSelect(item));
    onClose();
  };

  const handleAddTask = async (e) => {
    e.preventDefault();
    const title = newTaskTitle.trim();
    if (!title) {
      setAddError(t("taskPicker.taskTitleRequired"));
      return;
    }
    if (!bundle?._id || !onBundleUpdated) return;
    setAddError("");
    setAddSaving(true);
    try {
      const res = await axios.post(`/task-bundles/${bundle._id}/tasks/ad-hoc`, {
        name: title,
        description: newTaskDescription.trim() || "",
        ...(bundle.projectId ? { projectId: bundle.projectId } : {})
      });
      onBundleUpdated(res.data.bundle ?? res.data);
      setNewTaskTitle("");
      setNewTaskDescription("");
      setShowAddTaskForm(false);
    } catch (err) {
      console.error("Error adding task:", err);
      setAddError(err.response?.data?.message || "Failed to add task");
    } finally {
      setAddSaving(false);
    }
  };

  const handleAddSubtask = async (e, taskIdx) => {
    e.preventDefault();
    const title = newSubtaskTitle.trim();
    if (!title) {
      setAddError(t("taskPicker.subtaskTitleRequired"));
      return;
    }
    if (!bundle?._id || !onBundleUpdated) return;
    setAddError("");
    setAddSaving(true);
    try {
      const res = await axios.post(
        `/task-bundles/${bundle._id}/tasks/${taskIdx}/subtasks`,
        { title }
      );
      onBundleUpdated(res.data);
      setNewSubtaskTitle("");
      setAddSubtaskForTaskIdx(null);
    } catch (err) {
      console.error("Error adding subtask:", err);
      setAddError(err.response?.data?.message || "Failed to add subtask");
    } finally {
      setAddSaving(false);
    }
  };

  const handleDeleteTask = async (taskIdx, taskTitle) => {
    if (!bundle?._id || !onBundleUpdated) return;
    if (!window.confirm(`Delete task "${taskTitle}"?${bundle.tasks[taskIdx]?.subtasks?.length ? " This will also remove all its subtasks." : ""}`)) return;
    setDeletingTaskIdx(taskIdx);
    setAddError("");
    try {
      const res = await axios.delete(`/task-bundles/${bundle._id}/tasks/${taskIdx}`);
      onBundleUpdated(res.data);
    } catch (err) {
      console.error("Error deleting task:", err);
      setAddError(err.response?.data?.message || "Failed to delete task");
    } finally {
      setDeletingTaskIdx(null);
    }
  };

  const handleDeleteSubtask = async (taskIdx, subtaskIdx, subtaskTitle) => {
    if (!bundle?._id || !onBundleUpdated) return;
    if (!window.confirm(`Delete subtask "${subtaskTitle}"?`)) return;
    setDeletingSubtask({ taskIdx, subtaskIdx });
    setAddError("");
    try {
      const res = await axios.delete(`/task-bundles/${bundle._id}/tasks/${taskIdx}/subtasks/${subtaskIdx}`);
      onBundleUpdated(res.data);
    } catch (err) {
      console.error("Error deleting subtask:", err);
      setAddError(err.response?.data?.message || "Failed to delete subtask");
    } finally {
      setDeletingSubtask(null);
    }
  };

  const TrashIcon = ({ onClick, disabled, className = "" }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`p-1.5 text-gray-400 hover:text-red-600 rounded hover:bg-red-50 disabled:opacity-50 ${className}`}
      title={t("taskPicker.delete")}
      aria-label={t("taskPicker.delete")}
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
      </svg>
    </button>
  );

  if (!isOpen) return null;

  // Rendered both under the task list and under the empty state, so a project that is set
  // up for task reporting but has no tasks yet is not a dead end.
  const bundleActions = (
    <>
      {canEditBundle && (
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          {addError && <p className="text-sm text-red-600 mb-2">{addError}</p>}
          {showAddTaskForm ? (
            <form onSubmit={handleAddTask} className="space-y-2">
              <input
                type="text"
                value={newTaskTitle}
                onChange={(e) => setNewTaskTitle(e.target.value)}
                placeholder={t("taskPicker.taskTitlePlaceholder")}
                className="w-full px-3 py-2 border rounded-lg text-sm"
                disabled={addSaving}
                autoFocus
              />
              <textarea
                value={newTaskDescription}
                onChange={(e) => setNewTaskDescription(e.target.value)}
                placeholder={t("taskPicker.descriptionOptionalPlaceholder")}
                rows={2}
                className="w-full px-3 py-2 border rounded-lg text-sm"
                disabled={addSaving}
              />
              <div className="flex gap-2">
                <button type="submit" disabled={addSaving} className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
                  {addSaving ? t("taskPicker.adding") : t("taskPicker.addTask")}
                </button>
                <button type="button" onClick={() => { setShowAddTaskForm(false); setNewTaskTitle(""); setNewTaskDescription(""); setAddError(""); }} className="px-3 py-1.5 text-gray-600 text-sm hover:text-gray-800">
                  {t("taskPicker.cancel")}
                </button>
              </div>
            </form>
          ) : (
            <button
              type="button"
              onClick={() => { setShowAddTaskForm(true); setAddError(""); }}
              className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t("taskPicker.addTask")}
            </button>
          )}
        </div>
      )}
      {canReportMissingTask && (
        <div className="p-4 border-t border-gray-200">
          <button
            type="button"
            onClick={() => onReportMissingTask(searchQuery.trim())}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed border-blue-300 bg-blue-50 text-blue-700 font-medium text-sm hover:bg-blue-100 hover:border-blue-400 transition-colors"
          >
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {t("reportFlags.reportMissingTask")}
          </button>
        </div>
      )}
    </>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />
      
      {/* Modal - same width as Create custom task modal (max-w-3xl) */}
      <div className="relative bg-white w-full max-w-3xl h-[80vh] sm:h-auto sm:max-h-[90vh] rounded-t-2xl sm:rounded-2xl flex flex-col overflow-hidden shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">
            {selectedItems.length > 0 ? t("taskPicker.selectedCount", { count: selectedItems.length }) : t("taskPicker.selectTasks")}
          </h2>
          <div className="flex items-center gap-2">
            {selectedItems.length > 0 && (
              <button
                onClick={handleConfirmSelection}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
              >
                {t("taskPicker.add")}
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 -mr-2 text-gray-500 hover:text-gray-700"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="p-4 border-b">
          <input
            type="text"
            placeholder={t("taskPicker.searchPlaceholder")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoFocus
          />
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          <button
            className={`flex-1 py-3 text-sm font-medium ${
              activeTab === "recent"
                ? "text-blue-600 border-b-2 border-blue-600"
                : "text-gray-500"
            }`}
            onClick={() => setActiveTab("recent")}
          >
            {t("taskPicker.recent")} ({filteredRecentTasks.length})
          </button>
          <button
            className={`flex-1 py-3 text-sm font-medium ${
              activeTab === "all"
                ? "text-blue-600 border-b-2 border-blue-600"
                : "text-gray-500"
            }`}
            onClick={() => setActiveTab("all")}
          >
            {t("taskPicker.allTasks")}
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {searchQuery.trim() ? (
            // Search results
            <div className="p-2">
              {filteredItems.length === 0 ? (
                <div className="py-8 px-2 text-center space-y-3">
                  <p className="text-gray-500">{t("taskPicker.noTasksFound")}</p>
                  {canReportMissingTask && (
                    <button
                      type="button"
                      onClick={() => onReportMissingTask(searchQuery.trim())}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-dashed border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100 hover:border-blue-400 font-medium text-sm transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      {t("reportFlags.reportMissingTaskNamed", { name: searchQuery.trim() })}
                    </button>
                  )}
                </div>
              ) : (
                filteredItems.map((item, idx) => {
                  const selected = isItemSelected(item);
                  return (
                    <button
                      key={`${item.taskId}-${item.subtaskId || "task"}-${idx}`}
                      onClick={() => toggleItemSelection(item)}
                      className={`w-full text-left p-4 rounded-lg mb-1 flex items-center gap-3 ${
                        selected ? "bg-blue-50 border border-blue-200" : "hover:bg-gray-50 border border-transparent"
                      }`}
                    >
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                        selected ? "bg-blue-600 border-blue-600" : "border-gray-300"
                      }`}>
                        {selected && (
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="font-medium text-gray-900">{displayText(item, "title")}</div>
                          {item.billingModel && (
                            <span className="text-xs text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                              {item.billingModel === "hourly" ? t("taskPicker.billingHourly") : item.billingModel === "fixed_price" ? t("taskPicker.billingFixedPrice") : ""}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {item.type === "subtask" && item.taskTitleDisplay && (
                            <span className="text-sm text-gray-500">{item.taskTitleDisplay}</span>
                          )}
                          {formatHoursDisplay(item.trackedHours, item.estimatedHours) && (
                            <span className={`text-xs ${
                              item.estimatedHours && item.trackedHours > item.estimatedHours 
                                ? 'text-red-600' 
                                : 'text-gray-500'
                            }`}>
                              {formatHoursDisplay(item.trackedHours, item.estimatedHours)}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          ) : activeTab === "recent" ? (
            // Recent tasks with expandable details
            <div className="p-2">
              {filteredRecentTasks.length === 0 ? (
                <p className="text-center text-gray-500 py-8">{t("taskPicker.noRecentTasks")}</p>
              ) : (
                filteredRecentTasks.map((item, idx) => {
                  const isExpanded = expandedRecentItem === idx;
                  const selected = isItemSelected(item);
                  const itemKey = `recent-${idx}`;
                  return (
                    <div
                      key={itemKey}
                      className={`mb-2 border rounded-lg overflow-hidden ${
                        selected ? "border-blue-200 bg-blue-50" : "border-gray-200"
                      }`}
                    >
                      <div className="flex items-center">
                        {/* Checkbox */}
                        <button
                          onClick={() => toggleItemSelection(item)}
                          className="p-3 flex-shrink-0"
                        >
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                            selected ? "bg-blue-600 border-blue-600" : "border-gray-300"
                          }`}>
                            {selected && (
                              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                        </button>
                        {/* Main clickable area for selection */}
                        <button
                          onClick={() => toggleItemSelection(item)}
                          className="flex-1 text-left py-3 pr-2"
                        >
                          <div className="flex items-center gap-2">
                            <div className="font-medium text-gray-900 text-sm">{displayText(item, "title")}</div>
                            {item.billingModel && (
                              <span className="text-xs text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded">
                                {item.billingModel === "hourly" ? t("taskPicker.billingHourly") : item.billingModel === "fixed_price" ? t("taskPicker.billingFixedPrice") : ""}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1 mt-0.5 text-xs text-gray-500">
                            {item.type === "subtask" && (item.taskTitleDisplay || item.taskTitle) && (
                              <>
                                <span className="truncate max-w-[120px]">{item.taskTitleDisplay || item.taskTitle}</span>
                                <span>·</span>
                              </>
                            )}
                            {item.taskSetName && (
                              <span className="truncate max-w-[100px]">{item.taskSetName}</span>
                            )}
                          </div>
                        </button>
                        {/* Expand button for description */}
                        {(displayText(item, "description") || item.description) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedRecentItem(isExpanded ? null : idx);
                            }}
                            className="p-3 text-gray-400 hover:text-gray-600 border-l border-gray-200"
                          >
                            <svg
                              className={`w-4 h-4 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                        )}
                      </div>
                      {/* Expanded description */}
                      {isExpanded && (displayText(item, "description") || item.description) && (
                        <div className="px-3 pb-3 pt-1 border-t border-gray-100 bg-gray-50">
                          <p className="text-xs text-gray-600">{displayText(item, "description") || item.description}</p>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          ) : (
            // All tasks grouped by task set
            <div className="p-2">
              {groupedTasks.length === 0 ? (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <p className="text-center text-gray-500 py-8 px-4">{t("taskPicker.noTasksAvailable")}</p>
                  {bundleActions}
                </div>
              ) : (
                // Task Set container
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  {/* Task Set Header */}
                  <button
                    onClick={() => toggleTaskSetExpand(bundle._id)}
                    className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100"
                  >
                    <div className="flex items-center gap-2">
                      <FaTasks className="w-5 h-5 text-gray-500 flex-shrink-0" />
                      <span className="font-semibold text-gray-900">
                        {bundle.taskSetRef?.name || t("taskPicker.otherTasks")}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500 flex items-center gap-1.5">
                        <FaTasks className="w-4 h-4 flex-shrink-0" aria-hidden />
                        <span>{groupedTasks.length}</span>
                        <span className="hidden md:inline">{t("taskPicker.tasksLabel")}</span>
                      </span>
                      <svg
                        className={`w-5 h-5 text-gray-400 transition-transform ${
                          expandedTaskSets[bundle._id] ? "rotate-180" : ""
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>

                  {/* Task Set Content */}
                  {expandedTaskSets[bundle._id] && (
                    <div className="divide-y divide-gray-100">
                      {groupedTasks.map((task) => {
                        // Create task item for selection
                        const taskItem = {
                          type: "task",
                          taskId: task._id,
                          taskIdx: task.taskIdx,
                          title: task.title,
                          description: task.description || "",
                          displayName: task.title,
                          titleLanguage: task.titleLanguage,
                          bundleId: task.bundleId,
                          taskSetName: bundle?.taskSetRef?.name || "",
                          showDescriptionInTimeReport: task.showDescriptionInTimeReport || false,
                          requireDescriptionInTimeReport: task.requireDescriptionInTimeReport || false,
                          billingModel: task.billingModel || null,
                          estimatedHours: task.estimatedHours,
                          trackedHours: task.trackedHours || 0,
                        };
                        const taskSelected = isItemSelected(taskItem);
                        
                        return (
                          <div key={task._id}>
                            {task.subtasks && task.subtasks.length > 0 ? (
                              // Task with subtasks - selectable and expandable
                              <>
                                <div className="flex items-center p-4 hover:bg-gray-50">
                                  {/* Checkbox for task selection */}
                                  <button
                                    onClick={() => toggleItemSelection(taskItem)}
                                    className="flex-shrink-0 mr-3"
                                  >
                                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                      taskSelected ? "bg-blue-600 border-blue-600" : "border-gray-300"
                                    }`}>
                                      {taskSelected && (
                                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                        </svg>
                                      )}
                                    </div>
                                  </button>
                                  {/* Task name - clickable for selection */}
                                  <button
                                    onClick={() => toggleItemSelection(taskItem)}
                                    className="flex-1 text-left"
                                  >
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium text-gray-900">{displayText(task, "title")}</span>
                                      {task.billingModel && (
                                        <span className="text-xs text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded">
                                          {task.billingModel === "hourly" ? t("taskPicker.billingHourly") : task.billingModel === "fixed_price" ? t("taskPicker.billingFixedPrice") : ""}
                                        </span>
                                      )}
                                      {formatHoursDisplay(task.trackedHours, task.estimatedHours) && (
                                        <span className={`text-xs ${
                                          task.estimatedHours && (task.trackedHours || 0) > task.estimatedHours 
                                            ? 'text-red-600' 
                                            : 'text-gray-500'
                                        }`}>
                                          {formatHoursDisplay(task.trackedHours, task.estimatedHours)}
                                        </span>
                                      )}
                                    </div>
                                  </button>
                                  {/* Expand/collapse button */}
                                  <button
                                    onClick={() => toggleTaskExpand(task._id)}
                                    className="flex items-center gap-2 ml-2 text-gray-500 hover:text-gray-700"
                                  >
                                    <FaListUl className="w-4 h-4 flex-shrink-0" aria-hidden />
                                    <span className="text-sm">{task.subtasks.length}</span>
                                    <span className="hidden md:inline text-sm">{t("taskPicker.subtasksLabel")}</span>
                                    <svg
                                      className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${
                                        expandedTasks[task._id] ? "rotate-180" : ""
                                      }`}
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                  </button>
                                  {canEditBundle && (
                                    <>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setAddSubtaskForTaskIdx(addSubtaskForTaskIdx === task.taskIdx ? null : task.taskIdx);
                                          setNewSubtaskTitle("");
                                          setAddError("");
                                        }}
                                        className="ml-2 text-sm text-blue-600 hover:text-blue-700"
                                      >
                                        {t("taskPicker.addSubtask")}
                                      </button>
                                      <TrashIcon
                                        onClick={(e) => { e.stopPropagation(); handleDeleteTask(task.taskIdx, task.title); }}
                                        disabled={isDeletingTask(task.taskIdx)}
                                      />
                                    </>
                                  )}
                                </div>
                                {expandedTasks[task._id] && (
                                  <div className="pl-4 border-l-2 border-gray-200 ml-4 pb-2">
                                    {task.subtasks.map((subtask, subtaskIdx) => {
                                      const subtaskItem = {
                                        type: "subtask",
                                        taskId: task._id,
                                        taskIdx: task.taskIdx,
                                        subtaskId: subtask._id,
                                        subtaskIdx,
                                        taskTitle: task.title,
                                        title: subtask.title,
                                        description: subtask.description || "",
                                        displayName: `${task.title} > ${subtask.title}`,
                                        titleLanguage: subtask.titleLanguage || task.titleLanguage,
                                        bundleId: task.bundleId,
                                        taskSetName: bundle?.taskSetRef?.name || "",
                                        showDescriptionInTimeReport: subtask.showDescriptionInTimeReport || false,
                                        requireDescriptionInTimeReport: subtask.requireDescriptionInTimeReport || false,
                                        billingModel: subtask.billingModel || task.billingModel || null,
                                        estimatedHours: subtask.estimatedHours,
                                        trackedHours: subtask.trackedHours || 0,
                                      };
                                      const selected = isItemSelected(subtaskItem);
                                      const subtaskBillingModel = subtask.billingModel || task.billingModel;
                                      return (
                                        <div
                                          key={subtask._id}
                                          className={`flex items-center gap-1 p-3 rounded-lg ${
                                            selected ? "bg-blue-50" : "hover:bg-gray-50"
                                          }`}
                                        >
                                          <button
                                            onClick={() => toggleItemSelection(subtaskItem)}
                                            className="flex-1 min-w-0 text-left flex items-center gap-3"
                                          >
                                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                                              selected ? "bg-blue-600 border-blue-600" : "border-gray-300"
                                            }`}>
                                              {selected && (
                                                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                </svg>
                                              )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                              <div className="flex items-center gap-2">
                                                <span className="text-gray-700">{displayText(subtask, "title")}</span>
                                                {subtaskBillingModel && (
                                                  <span className="text-xs text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded">
                                                    {subtaskBillingModel === "hourly" ? t("taskPicker.billingHourly") : subtaskBillingModel === "fixed_price" ? t("taskPicker.billingFixedPrice") : ""}
                                                  </span>
                                                )}
                                                {formatHoursDisplay(subtask.trackedHours, subtask.estimatedHours) && (
                                                  <span className={`text-xs ${
                                                    subtask.estimatedHours && (subtask.trackedHours || 0) > subtask.estimatedHours 
                                                      ? 'text-red-600' 
                                                      : 'text-gray-500'
                                                  }`}>
                                                    {formatHoursDisplay(subtask.trackedHours, subtask.estimatedHours)}
                                                  </span>
                                                )}
                                              </div>
                                            </div>
                                          </button>
                                          {canEditBundle && (
                                            <TrashIcon
                                              onClick={(e) => { e.stopPropagation(); handleDeleteSubtask(task.taskIdx, subtaskIdx, subtask.title); }}
                                              disabled={isDeletingSubtask(task.taskIdx, subtaskIdx)}
                                            />
                                          )}
                                        </div>
                                      );
                                    })}
                                    {canEditBundle && addSubtaskForTaskIdx === task.taskIdx && (
                                      <form onSubmit={(e) => handleAddSubtask(e, task.taskIdx)} className="p-3 mt-1 border-t border-gray-100">
                                        {addError && <p className="text-sm text-red-600 mb-2">{addError}</p>}
                                        <input
                                          type="text"
                                          value={newSubtaskTitle}
                                          onChange={(e) => setNewSubtaskTitle(e.target.value)}
                                          placeholder={t("taskPicker.subtaskTitlePlaceholder")}
                                          className="w-full px-3 py-2 border rounded-lg text-sm mb-2"
                                          disabled={addSaving}
                                          autoFocus
                                        />
                                        <div className="flex gap-2">
                                          <button type="submit" disabled={addSaving} className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
                                            {addSaving ? t("taskPicker.adding") : t("taskPicker.addSubtask")}
                                          </button>
                                          <button type="button" onClick={() => { setAddSubtaskForTaskIdx(null); setNewSubtaskTitle(""); setAddError(""); }} className="px-3 py-1.5 text-gray-600 text-sm hover:text-gray-800">
                                            {t("taskPicker.cancel")}
                                          </button>
                                        </div>
                                      </form>
                                    )}
                                  </div>
                                )}
                              </>
                            ) : (
                            // Task without subtasks - directly selectable
                            (() => {
                              const taskItem = {
                                type: "task",
                                taskId: task._id,
                                taskIdx: task.taskIdx,
                                title: task.title,
                                description: task.description || "",
                                displayName: task.title,
                                titleLanguage: task.titleLanguage,
                                bundleId: task.bundleId,
                                taskSetName: bundle?.taskSetRef?.name || "",
                                showDescriptionInTimeReport: task.showDescriptionInTimeReport || false,
                                requireDescriptionInTimeReport: task.requireDescriptionInTimeReport || false,
                                billingModel: task.billingModel || null,
                                estimatedHours: task.estimatedHours,
                                trackedHours: task.trackedHours || 0,
                              };
                              const selected = isItemSelected(taskItem);
                              return (
                                <div>
                                  <div className="flex items-center p-4 hover:bg-gray-50">
                                    <button
                                      onClick={() => toggleItemSelection(taskItem)}
                                      className={`flex-1 text-left flex items-center gap-3 ${
                                        selected ? "bg-blue-50" : ""
                                      }`}
                                    >
                                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                                        selected ? "bg-blue-600 border-blue-600" : "border-gray-300"
                                      }`}>
                                        {selected && (
                                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                          </svg>
                                        )}
                                      </div>
                                        <div className="flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <span className="font-medium text-gray-900">{displayText(task, "title")}</span>
                                          {task.billingModel && (
                                            <span className="text-xs text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded">
                                              {task.billingModel === "hourly" ? t("taskPicker.billingHourly") : task.billingModel === "fixed_price" ? t("taskPicker.billingFixedPrice") : ""}
                                            </span>
                                          )}
                                          {formatHoursDisplay(task.trackedHours, task.estimatedHours) && (
                                            <span className={`text-xs ${
                                              task.estimatedHours && (task.trackedHours || 0) > task.estimatedHours 
                                                ? 'text-red-600' 
                                                : 'text-gray-500'
                                            }`}>
                                              {formatHoursDisplay(task.trackedHours, task.estimatedHours)}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </button>
                                    {canEditBundle && (
                                      <>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setAddSubtaskForTaskIdx(addSubtaskForTaskIdx === task.taskIdx ? null : task.taskIdx);
                                            setNewSubtaskTitle("");
                                            setAddError("");
                                          }}
                                          className="text-sm text-blue-600 hover:text-blue-700"
                                        >
                                          Add subtask
                                        </button>
                                        <TrashIcon
                                          onClick={(e) => { e.stopPropagation(); handleDeleteTask(task.taskIdx, task.title); }}
                                          disabled={isDeletingTask(task.taskIdx)}
                                        />
                                      </>
                                    )}
                                  </div>
                                  {canEditBundle && addSubtaskForTaskIdx === task.taskIdx && (
                                    <form onSubmit={(e) => handleAddSubtask(e, task.taskIdx)} className="px-4 pb-3 pt-0 border-t border-gray-100">
                                      {addError && <p className="text-sm text-red-600 mb-2">{addError}</p>}
                                      <input
                                        type="text"
                                        value={newSubtaskTitle}
                                        onChange={(e) => setNewSubtaskTitle(e.target.value)}
                                        placeholder="Subtask title"
                                        className="w-full px-3 py-2 border rounded-lg text-sm mb-2"
                                        disabled={addSaving}
                                        autoFocus
                                      />
                                      <div className="flex gap-2">
                                        <button type="submit" disabled={addSaving} className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
                                          {addSaving ? "Adding…" : "Add subtask"}
                                        </button>
                                        <button type="button" onClick={() => { setAddSubtaskForTaskIdx(null); setNewSubtaskTitle(""); setAddError(""); }} className="px-3 py-1.5 text-gray-600 text-sm hover:text-gray-800">
                                          Cancel
                                        </button>
                                      </div>
                                    </form>
                                  )}
                                </div>
                              );
                            })()
                          )}
                          </div>
                        );
                      })}
                      {bundleActions}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TaskPickerModal;
